import React from 'react'
import { useQuery } from 'react-query'
import { X, SlidersHorizontal, CalendarIcon, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { format } from 'date-fns'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import { taskService } from '@/services/taskService'

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

const MultiSelect = ({ label, options, selected, onToggle, className }) => {
  const selectedArr = selected ? selected.split(',') : []
  const count = selectedArr.length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-8 text-sm font-normal gap-1.5', count > 0 && 'border-primary text-primary', className)}
        >
          {label}
          {count > 0 && (
            <span className="ml-1 rounded-full bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 leading-none">
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="max-h-56 overflow-y-auto">
          {options.map((opt) => {
            const isChecked = selectedArr.includes(opt.value)
            return (
              <button
                key={opt.value}
                onClick={() => onToggle(opt.value)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent transition-colors text-left"
              >
                <span className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
                  isChecked ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'
                )}>
                  {isChecked && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

const TaskFilters = ({ filters, onChange, statuses = [], users = [], hideLabel = false, hideStatus = false, projectId }) => {
  const { data: typesData } = useQuery(
    'task-types',
    () => taskService.getTaskTypes(),
    { staleTime: 60000 * 5 }
  )
  const types = typesData?.data?.types || []
  const typeOptions = types.map(t => ({ value: String(t.id), label: t.name }))

  // Label + epic filters need the project's labels and epics
  const { data: labelsData } = useQuery(
    ['projects', String(projectId), 'labels'],
    () => taskService.getProjectLabels(projectId),
    { enabled: !!projectId, staleTime: 30000 }
  )
  const epicTypeIds = types.filter(t => t.hierarchy_level === 1).map(t => t.id).join(',')
  const { data: epicsData } = useQuery(
    ['projects', String(projectId), 'tasks', { epics: epicTypeIds }],
    () => taskService.getTasks(projectId, { type_id: epicTypeIds }),
    { enabled: !!projectId && !!epicTypeIds, staleTime: 30000 }
  )
  const labelOptions = (labelsData?.data?.labels || []).map(l => ({ value: l.label, label: `${l.label} (${l.count})` }))
  const epicOptions = [
    { value: 'none', label: 'No epic' },
    ...(epicsData?.data?.tasks || []).map(e => ({ value: String(e.id), label: e.title }))
  ]
  const clear = () => onChange({})

  const hasFilters = Object.values(filters).some(Boolean)

  const getUserName = (user) =>
    user.employee ? `${user.employee.first_name} ${user.employee.last_name}` : user.email

  const toggleValue = (key, value) => {
    const current = filters[key] ? filters[key].split(',') : []
    const idx = current.indexOf(value)
    if (idx >= 0) {
      current.splice(idx, 1)
    } else {
      current.push(value)
    }
    onChange({ ...filters, [key]: current.length > 0 ? current.join(',') : undefined })
  }

  const dateFrom = filters.due_date_from ? new Date(filters.due_date_from + 'T00:00:00') : undefined
  const dateTo = filters.due_date_to ? new Date(filters.due_date_to + 'T00:00:00') : undefined
  const hasDateFilter = dateFrom || dateTo

  const handleDateRangeSelect = (range) => {
    onChange({
      ...filters,
      due_date_from: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
      due_date_to: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
    })
  }

  const formatDateRange = () => {
    if (dateFrom && dateTo) return `${format(dateFrom, 'MMM d')} – ${format(dateTo, 'MMM d')}`
    if (dateFrom) return `From ${format(dateFrom, 'MMM d')}`
    if (dateTo) return `Until ${format(dateTo, 'MMM d')}`
    return 'Due date'
  }

  const statusOptions = statuses.map(s => ({ value: String(s.id), label: s.name }))
  const assigneeOptions = users.map(u => ({ value: String(u.id), label: getUserName(u) }))
  const priorityOptions = PRIORITIES

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!hideLabel && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span>Filters:</span>
        </div>
      )}

      {/* Status */}
      {!hideStatus && (
        <MultiSelect
          label="Status"
          options={statusOptions}
          selected={filters.status_id}
          onToggle={(v) => toggleValue('status_id', v)}
        />
      )}

      {/* Assignee */}
      <MultiSelect
        label="Assignee"
        options={assigneeOptions}
        selected={filters.assignee_id}
        onToggle={(v) => toggleValue('assignee_id', v)}
      />

      {/* Priority */}
      <MultiSelect
        label="Priority"
        options={priorityOptions}
        selected={filters.priority}
        onToggle={(v) => toggleValue('priority', v)}
      />

      {/* Type */}
      <MultiSelect
        label="Type"
        options={typeOptions}
        selected={filters.type_id}
        onToggle={(v) => toggleValue('type_id', v)}
      />

      {/* Labels */}
      {projectId && labelOptions.length > 0 && (
        <MultiSelect
          label="Label"
          options={labelOptions}
          selected={filters.labels}
          onToggle={(v) => toggleValue('labels', v)}
        />
      )}

      {/* Epic */}
      {projectId && epicOptions.length > 1 && (
        <MultiSelect
          label="Epic"
          options={epicOptions}
          selected={filters.parent_id}
          onToggle={(v) => toggleValue('parent_id', v)}
        />
      )}

      {/* Due date range */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('h-8 text-sm font-normal gap-1.5', hasDateFilter && 'border-primary text-primary')}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {formatDateRange()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={{ from: dateFrom, to: dateTo }}
            onSelect={handleDateRangeSelect}
            numberOfMonths={2}
            initialFocus
          />
          {hasDateFilter && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => handleDateRangeSelect({ from: undefined, to: undefined })}
              >
                Clear dates
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Clear all */}
      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-muted-foreground" onClick={clear}>
          <X className="h-3.5 w-3.5 mr-1" />
          Clear
        </Button>
      )}
    </div>
  )
}

export default TaskFilters
